import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import FuelPriceOutputComponent from '../output/FuelPriceOutputComponent';

// Convert snake_case / underscore names to Title Case ("andhra_pradesh" → "Andhra Pradesh")
const toTitleCase = (str) =>
    String(str).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Extract fuel states from various possible Surepass response shapes
const extractFuelStates = (responseData) => {
    const d = responseData?.data?.data?.data
        ?? responseData?.data?.data
        ?? responseData?.data
        ?? responseData;
    if (!d) return [];

    const toOption = (s) => {
        if (typeof s === 'object') {
            const raw = s.state_name || s.name || s.state || '';
            return { label: toTitleCase(raw), value: raw.toLowerCase() };
        }
        return { label: toTitleCase(s), value: String(s).toLowerCase() };
    };

    if (Array.isArray(d.states))     return d.states.map(toOption).filter(o => o.value);
    if (Array.isArray(d.state_list)) return d.state_list.map(toOption).filter(o => o.value);
    if (Array.isArray(d))            return d.map(toOption).filter(o => o.value);

    // Fallback: collect first array-valued key
    const arrayEntry = Object.values(d).find((v) => Array.isArray(v) && v.length > 0);
    if (arrayEntry) return arrayEntry.map(toOption).filter(o => o.value);
    return [];
};

const FuelPriceFlowComponent = () => {
    const { stateListServiceId, fuelServiceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [states, setStates] = useState([]);
    const [loadingStates, setLoadingStates] = useState(true);
    const [stateLoadError, setStateLoadError] = useState(false);

    const [stateDropdown, setStateDropdown] = useState(null);
    const [stateText, setStateText] = useState('');
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Load Fuel Price service credits
    useEffect(() => {
        if (!fuelServiceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${fuelServiceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [fuelServiceId]);

    // Auto-fetch state list on mount
    useEffect(() => {
        if (!stateListServiceId) { setLoadingStates(false); setStateLoadError(true); return; }
        setLoadingStates(true);
        axiosInstance.get('/surepass/utilities/fuel-price/state-list', {
            params: { api_provider_service_id: parseInt(stateListServiceId, 10) },
        })
            .then((r) => {
                const extracted = extractFuelStates(r.data);
                if (extracted.length > 0) {
                    setStates(extracted);
                } else {
                    setStateLoadError(true);
                }
            })
            .catch(() => setStateLoadError(true))
            .finally(() => setLoadingStates(false));
    }, [stateListServiceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const rd = error.response?.data;
        let detail;
        if (s === 402) detail = `Insufficient credits. Required: ${rd?.required_credits ?? 'N/A'}, Available: ${rd?.current_balance ?? 'N/A'}`;
        else if (s === 422) { const e = rd?.errors; detail = e ? Object.values(e)[0]?.[0] : rd?.message || 'Validation failed'; }
        else if (s === 429) detail = 'Too many requests. Please wait before trying again.';
        else detail = rd?.message || error.message || summary;
        // 4s is not long enough to read a "provider unavailable, no credits
        // used" sentence, which is the common case for this report.
        toast.current?.show({ severity: 'error', summary, detail, life: detail?.length > 60 ? 8000 : 4000 });
    };

    const validate = () => {
        const next = {};
        const selectedState = stateDropdown || stateText.trim();
        if (!selectedState) next.state = 'State is required';
        if (!consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doSubmit = async () => {
        setLoading(true);
        const selectedState = stateDropdown || stateText.trim().toLowerCase();
        try {
            const res = await axiosInstance.post('/surepass/utilities/fuel-price', {
                state: selectedState,
                api_provider_service_id: parseInt(fuelServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Fetch failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Fuel price fetched', detail: 'Report ready below', life: 2000 });
            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${fuelServiceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (_) {}
        } catch (err) {
            showError(err, 'Fuel price fetch failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        const selectedState = stateDropdown || stateText.trim().toLowerCase();
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">State:</span> <span className="text-900 font-medium capitalize">{selectedState}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm fuel price fetch',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: doSubmit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const reset = () => {
        setReportData(null);
        setStateDropdown(null);
        setStateText('');
        setConsent(false);
        setErrors({});
        submittingRef.current = false;
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-dollar"
                        title="Fuel Price"
                        subtitle="Fetch latest fuel prices by state"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="fuelState" className="block font-medium text-sm mb-2">
                                            State <span className="text-red-500">*</span>
                                        </label>
                                        {loadingStates ? (
                                            <div className="flex align-items-center gap-2 p-2">
                                                <i className="pi pi-spinner pi-spin text-primary" />
                                                <span className="text-500 text-sm">Loading states...</span>
                                            </div>
                                        ) : states.length > 0 ? (
                                            <Dropdown
                                                id="fuelState"
                                                value={stateDropdown}
                                                options={states}
                                                onChange={(e) => { setStateDropdown(e.value); if (errors.state) setErrors((p) => ({ ...p, state: '' })); }}
                                                placeholder="Select a state"
                                                filter
                                                filterPlaceholder="Search state"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.state })}
                                            />
                                        ) : (
                                            <IconField iconPosition="left" className="w-full">
                                                <InputIcon className="pi pi-map-marker" />
                                                <InputText
                                                    id="fuelState"
                                                    value={stateText}
                                                    onChange={(e) => { setStateText(e.target.value || ''); if (errors.state) setErrors((p) => ({ ...p, state: '' })); }}
                                                    placeholder="e.g. kerala"
                                                    maxLength={50}
                                                    disabled={loading}
                                                    className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.state })}
                                                />
                                            </IconField>
                                        )}
                                        {stateLoadError && !loadingStates && <small className="text-orange-500 block mt-1"><i className="pi pi-exclamation-triangle mr-1" />Could not load states — enter manually</small>}
                                        {errors.state && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.state}</small>}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={consent}
                                    onChange={(e) => { setConsent(e.checked); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                                    error={errors.consent}
                                    reportType="fuel-price"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button type="submit" label="Fetch Fuel Price" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading || loadingStates} size="small" />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button label="Fetch Another" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={reset} />
                            </div>
                            <FuelPriceOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default FuelPriceFlowComponent;
